import {
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  inject,
  signal,
  Directive
} from '@angular/core';
import { Subscription, interval, Observable, EMPTY } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';
import { PaginatorComponent } from '@view/shared/molecules/paginator/paginator.component';
import {
  ButtonTypeEnum,
  ButtonSizeEnum
} from '@view/shared/molecules/button/buttonTypesEnum';
import { TableActionType } from '@view/shared/molecules/app-table/tableActionTypes';
import type { MetricItem } from '@view/shared/molecules/metrics-dashboard/metrics-dashboard.component';
import { ToastService } from '@view/shared/molecules/toast/service/toast.service';
import { IOrderMetricsPort } from '../../../domain/service/preclinical/order-metrics.port';
import type {
  SearchEvent,
  SearchFieldOption
} from '@view/shared/molecules/search-controls/search-controls.component';
import { SearchField } from '../../../domain/types/search-field.type';
import { OrderStatus } from '../../../domain/types/order-status.type';
import { IngestedOrdersQueryParams } from '@model/ingested-order/ingested-order.model';
import type { FilterCriteria } from '@model/filter/filter-options.model';
import { ExportModalState } from '../molecules/export-confirmation-modal/export-confirmation-modal.component';
import { ViewExpansionStateService } from '@helpers/state/view-expansion-state.service';
import { FrozenColumnsService } from '@helpers/table/frozen-columns.service';
import { INGESTED_ORDER_SEARCH_OPTIONS } from '../config/search-options.config';

@Directive()
export abstract class BaseOrdersListComponent implements OnInit, OnDestroy {
  private readonly LABEL_TODAY = 'Hoy';

  currentPage = 0;
  pageSize = 10;
  totalRecords = 0;

  protected metricsSubscription?: Subscription;
  protected autoRefreshSubscription?: Subscription;

  abstract paginator: PaginatorComponent | undefined;

  readonly ButtonTypeEnum = ButtonTypeEnum;
  readonly ButtonSizeEnum = ButtonSizeEnum;

  protected readonly viewExpansionState = inject(ViewExpansionStateService);

  isFrozenSidebarOpen = false;
  isFilterSidebarOpen = false;
  currentFilterCriteria: FilterCriteria | null = null;

  protected readonly _showSearchControls = signal(false);
  readonly showSearchControls = this._showSearchControls.asReadonly();

  protected currentSearchField: SearchField | string | null = null;
  protected currentSearchValue: string | null = null;

  protected readonly _isExportModalOpen = signal(false);
  readonly isExportModalOpen = this._isExportModalOpen.asReadonly();

  protected readonly _exportModalState = signal<ExportModalState>(
    ExportModalState.CONFIRMATION
  );
  readonly exportModalState = this._exportModalState.asReadonly();

  readonly ExportModalState = ExportModalState;

  searchFieldOptions: SearchFieldOption[] = INGESTED_ORDER_SEARCH_OPTIONS;

  dashboardMetrics: MetricItem[] = [];
  tableActions: TableActionType[] = [];

  protected abstract getOrderStatus(): OrderStatus;
  protected abstract getMetricsService(): IOrderMetricsPort;
  protected abstract getCdr(): ChangeDetectorRef;
  protected abstract getToastService(): ToastService;
  public abstract frozenColumnsService: FrozenColumnsService;

  ngOnInit(): void {
    this.loadMetrics();
    this.startAutoRefresh();
  }

  ngOnDestroy(): void {
    this.metricsSubscription?.unsubscribe();
    this.autoRefreshSubscription?.unsubscribe();
  }

  protected abstract loadOrdersInternal(): Observable<void> | void;

  protected startAutoRefresh(): void {
    const loadResult = this.loadOrdersInternal();

    if (loadResult instanceof Observable) {
      this.autoRefreshSubscription = interval(60000)
        .pipe(
          startWith(0),
          switchMap(() => {
            const result = this.loadOrdersInternal();
            return result instanceof Observable ? result : EMPTY;
          })
        )
        .subscribe();
    } else {
      this.autoRefreshSubscription = interval(60000)
        .pipe(startWith(0))
        .subscribe(() => this.loadOrdersInternal());
    }
  }

  protected loadMetrics(): void {
    this.metricsSubscription = this.getMetricsService()
      .getMetricsStream(this.getOrderStatus(), 60000)
      .subscribe((metrics) => {
        this.dashboardMetrics = [
          { label: this.LABEL_TODAY, value: this.LABEL_TODAY, isDate: true },
          { label: 'Total muestras', value: metrics.totalOrders },
          { label: 'Muestras ingresadas', value: metrics.ordersToday }
        ];
      });
  }

  protected buildBaseQueryParams(): {
    status: OrderStatus;
    page: number;
    size: number;
    searchField?: SearchField | string;
    searchValue?: string;
  } {
    const params: {
      status: OrderStatus;
      page: number;
      size: number;
      searchField?: SearchField | string;
      searchValue?: string;
    } = {
      status: this.getOrderStatus(),
      page: this.currentPage,
      size: this.pageSize
    };

    if (this.currentSearchField && this.currentSearchValue) {
      params.searchField = this.currentSearchField;
      params.searchValue = this.currentSearchValue;
    }

    return params;
  }

  protected addFilterParamsToQuery(
    queryParams: IngestedOrdersQueryParams
  ): IngestedOrdersQueryParams {
    if (!this.currentFilterCriteria) {
      return queryParams;
    }

    return {
      ...queryParams,
      ...(this.currentFilterCriteria.priority && {
        priority: this.currentFilterCriteria.priority
      }),
      ...(this.currentFilterCriteria.region && {
        region: this.currentFilterCriteria.region.map((r) => r.id)
      }),
      ...(this.currentFilterCriteria.sites && {
        sites: this.currentFilterCriteria.sites.map((s) => s.id)
      }),
      ...(this.currentFilterCriteria.examType && {
        examType: this.currentFilterCriteria.examType.map((e) => e.code)
      }),
      ...(this.currentFilterCriteria.startDate && {
        startDate: this.currentFilterCriteria.startDate
      }),
      ...(this.currentFilterCriteria.endDate && {
        endDate: this.currentFilterCriteria.endDate
      }),
      ...(this.currentFilterCriteria.reprocessCauses && {
        reprocessCauses: this.currentFilterCriteria.reprocessCauses.map((c) => c.id)
      })
    };
  }

  toggleExpansion(): void {
    this.viewExpansionState.toggle();
  }

  get isExpanded(): boolean {
    return this.viewExpansionState.isExpanded();
  }

  toggleFrozenColumnsSidebar(): void {
    this.isFrozenSidebarOpen = !this.isFrozenSidebarOpen;
  }

  closeFrozenColumnsSidebar(): void {
    this.isFrozenSidebarOpen = false;
  }

  toggleSearchControls(): void {
    this._showSearchControls.update((value) => !value);
  }

  handleSearch(event: SearchEvent): void {
    this.currentSearchField = event.searchField;
    this.currentSearchValue = event.searchValue;
    this.resetToFirstPageAndReload();
  }

  handleClearSearch(): void {
    this.currentSearchField = null;
    this.currentSearchValue = null;
    this.resetToFirstPageAndReload();
  }

  protected abstract resetToFirstPageAndReload(): void;

  toggleFilterSidebar(): void {
    this.isFilterSidebarOpen = !this.isFilterSidebarOpen;
  }

  closeFilterSidebar(): void {
    this.isFilterSidebarOpen = false;
  }

  handleApplyFilters(criteria: FilterCriteria): void {
    this.currentFilterCriteria = criteria;
    this.resetToFirstPageAndReload();
  }

  clearFilters(): void {
    this.currentFilterCriteria = null;
    this.resetToFirstPageAndReload();
  }

  get hasActiveFilters(): boolean {
    return this.currentFilterCriteria !== null;
  }

  openExportModal(): void {
    this._exportModalState.set(ExportModalState.CONFIRMATION);
    this._isExportModalOpen.set(true);
  }

  closeExportModal(): void {
    this._isExportModalOpen.set(false);
    setTimeout(() => {
      this._exportModalState.set(ExportModalState.CONFIRMATION);
    }, 300);
  }

  protected buildBaseExportQueryParams(): IngestedOrdersQueryParams {
    let params: IngestedOrdersQueryParams = {
      status: this.getOrderStatus()
    };

    params = this.addSearchParamsToExport(params);
    params = this.addFilterParamsToExport(params);

    return params;
  }

  protected addSearchParamsToExport(params: IngestedOrdersQueryParams): IngestedOrdersQueryParams {
    if (this.currentSearchField && this.currentSearchValue) {
      return {
        ...params,
        searchField: this.currentSearchField,
        searchValue: this.currentSearchValue
      };
    }
    return params;
  }

  protected addFilterParamsToExport(params: IngestedOrdersQueryParams): IngestedOrdersQueryParams {
    if (!this.currentFilterCriteria) {
      return params;
    }

    const criteria = this.currentFilterCriteria;

    return {
      ...params,
      ...(criteria.priority?.length && { priority: criteria.priority }),
      ...(criteria.region?.length && { region: criteria.region.map((r) => r.id) }),
      ...(criteria.sites?.length && { sites: criteria.sites.map((s) => s.id) }),
      ...(criteria.examType?.length && { examType: criteria.examType.map((e) => e.code) }),
      ...(criteria.startDate && { startDate: criteria.startDate }),
      ...(criteria.endDate && { endDate: criteria.endDate })
    };
  }
}
